import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import TrueImpactFileUploadBox, {
  TrueImpactFile,
} from './TrueImpactFileUploadBox';
import { FileUploadResponse } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';
import StoryFileRender from 'npo/pages/ReportBuilder/Components/Narrative/StoryFileRender';

interface TrueImpactFileUploadProps {
  title?: string;
  maxFileSize: number;
  acceptedFiles: string;
  initialFile: TrueImpactFile | undefined;
  acceptedFileExtensions: string[];
  handleFileUpload: (file: File | undefined) => Promise<FileUploadResponse>;
}

const TrueImpactFileUpload: React.FC<TrueImpactFileUploadProps> = ({
  title = 'Drag your photo here or click to upload',
  initialFile,
  maxFileSize,
  acceptedFileExtensions,
  acceptedFiles,
  handleFileUpload,
}) => {
  const [file, setFile] = useState<TrueImpactFile | undefined>(initialFile);

  const onFileUpload = useCallback(
    async (file: File | undefined) => {
      if (!file) {
        handleFileUpload(undefined);
        setFile(undefined);
        return;
      }
      if (!acceptedFileExtensions.includes(file.type)) {
        setFile(s => ({
          ...s,
          file: undefined,
          fileName: undefined,
          error: `Sorry, ${file.type || 'that'} file format isn’t supported.`,
        }));
        return;
      }
      if (file.size > maxFileSize) {
        setFile(s => ({
          ...s,
          file: undefined,
          fileName: undefined,
          error: `File is too big. Please try size under ${maxFileSize}MB`,
        }));
        return;
      }
      setFile(s => ({ ...s, isUploading: true }));
      const uploadResult = await handleFileUpload(file);
      if (uploadResult.success) {
        setFile(s => ({
          ...s,
          ...uploadResult,
          file,
          fileName: file.name,
          error: undefined,
          isUploading: false,
        }));
      } else {
        setFile(s => ({
          ...s,
          file: undefined,
          fileName: undefined,
          isUploading: false,
          error:
            uploadResult.error?.response?.data?.errors?.detail ||
            uploadResult.error?.message ||
            'Something went wrong while uploading.',
        }));
      }
    },
    [setFile, handleFileUpload],
  );

  return (
    <TrueImpactFileUploadBox
      width="100%"
      height="auto"
      CustomSelectedComponent={StoryFileRender}
      defaultFileText={title}
      file={file}
      acceptedFileString={acceptedFiles}
      handleFileUpload={onFileUpload}
    />
  );
};

export default TrueImpactFileUpload;
